/**
 * ── Centralized API Endpoints ─────────────────────────────────────────────────
 *
 *   Every API endpoint in Quick Discount lives here as a named constant.
 *   One source of truth — no more stringly-typed URLs scattered across actions.
 *
 * ── Usage ──────────────────────────────────────────────────────────────────────
 *
 *   import { API_ENDPOINTS, API_BASE_URL } from "../../utils/endpoints";
 *   import { api } from "../../utils/api";
 *
 *   // Static path
 *   api.get(API_ENDPOINTS.discounts.list)
 *
 *   // Parameterized path
 *   api.get(API_ENDPOINTS.discounts.byId(discountId))
 *
 *   // With auth token
 *   api.post(API_ENDPOINTS.discounts.create, body, {}, token)
 *
 *   // Full URL for non-API scenarios
 *   const url = `${API_BASE_URL}${API_ENDPOINTS.discounts.list}`
 * ────────────────────────────────────────────────────────────────────────────────
 */

/** Base URL — swap between local dev and production here */
export const API_BASE_URL = "https://quickdiscount.pythonanywhere.com";

// ── API Endpoints ──────────────────────────────────────────────────────────────
export const API_ENDPOINTS = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  auth: {
    signup:             "/users/signup/",
    login:              "/users/login/",
    token:              "/users/token/",
    tokenRefresh:       "/users/token/refresh/",
    logout:             "/users/logout/",
    passwordReset:      "/users/password-reset/",
    passwordResetVerify: (uidb64, token) => `/users/password-reset/${uidb64}/${token}/`,
    activate:           (uidb64, token) => `/users/activate/${uidb64}/${token}/`,
    deleteUser:         (userId) => `/users/delete/${userId}/`,
  },

  // ── Users ───────────────────────────────────────────────────────────────────
  users: {
    list:               "/users/",
    byId:               (id) => `/users/${id}/`,
  },

  // ── Profile ─────────────────────────────────────────────────────────────────
  profile: {
    get:                "/profile/",
    create:             "/profile/",
    update:             "/profile/update/",
  },

  // ── Discounts ───────────────────────────────────────────────────────────────
  discounts: {
    list:               "/discounts/",
    byId:               (id) => `/discounts/${id}/`,
    create:             "/discounts/",
    update:             (id) => `/discounts/${id}/`,
    delete:             (id) => `/discounts/${id}/`,
    detailView:         (id) => `/discounts/detail/${id}/`,
    allView:            "/discounts/all/",
    byOrganizer:        (organizerId) => `/discounts/organizer-discounts/${organizerId}`,

    // Categories
    categories:         "/discounts/categories/",
    categoryById:       (id) => `/discounts/categories/${id}/`,

    // Likes
    likes:              "/discounts/likes/",
    likesByDiscount:    (discountId) => `/discounts/likes/${discountId}/`,
    likeAdd:            "/discounts/likes/add/",
    likeDetail:         (id) => `/discounts/likes/detail/${id}/`,
    likeVerify:         (discountId) => `/discounts/likes/verify/0/${discountId}/`,
    likeDelete:         (id) => `/discounts/likes/delete/${id}/`,

    // Wishlist
    wishlist:           "/discounts/get-wishlist/",
    wishlistAdd:        "/discounts/add-to-wishlist/",
    wishlistRemove:     "/discounts/remove-from-wishlist/",
    wishlistItem:       (id) => `/discounts/wishlist/${id}/`,

    // Media
    media:              "/discounts/media/",
    mediaById:          (id) => `/discounts/media/${id}/`,
    mediaByDiscount:    (discountId) => `/discounts/media/files/${discountId}/`,

    // Packages
    packages:           "/discounts/packages/",
    packageById:        (id) => `/discounts/packages/${id}/`,
    packageDelete:      (id) => `/packages/${id}/`,       // legacy — no /discounts prefix

    // Tickets (on discount)
    ticketsByDiscount:  (discountId) => `/discounts/tickets/${discountId}/`,

    // User likes map
    userLikes:          "/discounts/user-likes/",
  },

  // ── Reviews ─────────────────────────────────────────────────────────────────
  reviews: {
    list:               "/reviews/",
    create:             "/reviews/create/",
    hasReviewed:        "/reviews/has-reviewed/",
    helpful:            (reviewId) => `/reviews/${reviewId}/vote-helpful/`,
    unhelpful:          (reviewId) => `/reviews/${reviewId}/vote-unhelpful/`,

    // Legacy / documented endpoints
    byDiscount:         (discountId) => `/discounts/reviews/`,
    byId:               (id) => `/discounts/reviews/${id}/`,
    update:             (id) => `/discounts/reviews/update/${id}/`,
    replyList:          "/discounts/reviews/",

    // Replies
    replies:            "/review-replies/",
  },

  // ── Organizers ──────────────────────────────────────────────────────────────
  organizers: {
    list:               "/discounts/organizers/",
    byId:               (id) => `/discounts/organizers/${id}/`,
    create:             "/discounts/organizers/",
    update:             (id) => `/discounts/organizers/${id}/`,
    delete:             (id) => `/discounts/organizers/${id}/`,
    userOrganizer:      "/discounts/user/organizer/",
    analytics:          (organizerId) => `/discounts/analytics/${organizerId}/`,

    // Followers
    followers:          "/discounts/organizers/followers/",
    followerAdd:        "/discounts/organizer/followers/add/",
    followerByOrganizer:(organizerId) => `/discounts/organizer/followers/${organizerId}/`,
    followerDetail:     (id) => `/discounts/organizer/followers/detail/${id}/`,
    followerVerify:     (organizerId) => `/discounts/organizer/followers/verify/0/${organizerId}/`,
    followerDelete:     (id) => `/discounts/organizer/followers/delete/${id}/`,
  },

  // ── Tokens ──────────────────────────────────────────────────────────────────
  tokens: {
    verify:             "/tokens/verify/",
    quickRedeem:        "/tokens/quick-redeem/",
    create:             "/tokens/",
    byDiscount:         (discountId) => `/tokens/discount/${discountId}/`,
    redeemed:           (discountId) => `/tokens/discount/${discountId}/redeemed/`,
  },

  // ── Tickets ─────────────────────────────────────────────────────────────────
  tickets: {
    list:               "/tickets/",
    byId:               (id) => `/tickets/${id}/`,
    create:             "/tickets/",
    add:                (discountId) => `/tickets/add/${discountId}/`,
    update:             (id) => `/tickets/update/${id}/`,
    delete:             (id) => `/tickets/${id}/`,
    userTickets:        "/tickets/user/",
  },

  // ── Orders ──────────────────────────────────────────────────────────────────
  orders: {
    list:               "/order/",
    byId:               (id) => `/order/${id}/`,
    create:             "/order/",
    update:             (id) => `/order/${id}/`,
    delete:             (id) => `/order/${id}/`,
    checkout:           "/order/checkout/",
    checkoutWithAddress:(addressPk) => `/order/checkout/${addressPk}/`,
    cart:               "/order/cart/",
    items:              "/order/items/",
    itemById:           (id) => `/order/items/${id}/`,
    itemCreate:         "/order/items/",
    itemUpdate:         (id) => `/order/items/${id}/`,
    itemDelete:         (id) => `/order/items/${id}/`,
  },

  // ── Addresses ───────────────────────────────────────────────────────────────
  addresses: {
    list:               "/address/",
    byId:               (id) => `/address/${id}/`,
    create:             "/address/",
    update:             (id) => `/address/${id}/`,
    patch:              (id) => `/address/${id}/`,
    delete:             (id) => `/address/${id}/`,
  },

  // ── Payments ────────────────────────────────────────────────────────────────
  payments: {
    paystackPay:        "/pay/",
    byId:               (id) => `/payment/${id}/`,
    update:             (id) => `/payment/${id}/`,
    delete:             (id) => `/payment/${id}/`,
    verify:             "/payment/verify/",
    momoView:           (orderId) => `/order/${orderId}/`,
    momoConfirm:        (paymentId) => `/momo/${paymentId}/confirm/`,
    choice:             "/choice/",
    choiceDiscount:     "/choice/discount/",
  },

  // ── Notifications ──────────────────────────────────────────────────────────
  notifications: {
    filter:             "/notifications/filter/",
    markAsRead:         (id) => `/notifications/${id}/mark-as-read/`,
    bulkUpdate:         "/notifications/bulk-update/",
  },

  // ── Search ──────────────────────────────────────────────────────────────────
  search: {
    query:              "/search/",
  },

  // ── Mail ───────────────────────────────────────────────────────────────────
  mail: {
    send:               "/mail/",
  },

  // ── USSD ───────────────────────────────────────────────────────────────────
  ussd: {
    service:            "/ussd/",
  },

  // ── Web Views ──────────────────────────────────────────────────────────────
  web: {
    home:               "/",
    help:               "/discounts/help",
    apiRoot:            "/api/",
    admin:              "/admin/",
  },
} ;

// ── Helper to build full URLs ─────────────────────────────────────────────────

/**
 * Get a fully-qualified API URL for any endpoint.
 * Useful for direct fetch() calls, redirects, or external links.
 *
 * @param {string} path - The API path (e.g. "/discounts/")
 * @returns {string} Full URL (e.g. "https://quickdiscount.pythonanywhere.com/discounts/")
 */
export function fullUrl(path) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${cleanPath}`;
}

/**
 * Build a full URL with query parameters appended.
 *
 * @param {string} path  - API path
 * @param {object} params - Key/value query parameters
 * @returns {string}
 */
export function urlWithParams(path, params = {}) {
  const base = fullUrl(path);
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  ).toString();
  return qs ? `${base}${base.includes("?") ? "&" : "?"}${qs}` : base;
}

export default API_ENDPOINTS;
